import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Select,
    MenuItem,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    styled,
} from '@mui/material';
import InfoHeader from '../components/InfoHeader';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import BasicMenu from '../components/BasicMenu';
import { AmbienteInternoPontoForte } from '../types/negocio/AmbienteInternoPontoForte';
import { ambienteInternoPontoForteService } from '../services/AmbienteInternoPontoForteService';
import toastService from '../utils/toastService';
import SaveStatusIndicator, { SaveStatus } from '../components/SaveStatusIndicator';
import { PrimaryButton, SecondaryButton } from '../components';
import LoadingIndicator from '../components/LoadingIndicator';
import InfoPopover from '../components/InfoPopover';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const impactOptions = [
    { value: 4, label: "(4) Muito" },
    { value: 3, label: "(3) Médio" },
    { value: 2, label: "(2) Pouco" },
    { value: 1, label: "(1) Nenhum" },
];

const diferencialOptions = [
    { value: 4, label: "(4) Grande" },
    { value: 3, label: "(3) Médio" },
    { value: 2, label: "(2) Pequeno" },
    { value: 1, label: "(1) Nenhum" },
];

const tendenciaOptions = [
    { value: 4, label: "(4) Ampliar" },
    { value: 3, label: "(3) Estabilizar" },
    { value: 2, label: "(2) Reduzir" },
    { value: 1, label: "(1) Regredir" },
];

const PontosFortes: React.FC = () => {
    const [rows, setRows] = useState<AmbienteInternoPontoForte[]>([]);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const [loading, setLoading] = useState<boolean>(true);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = await ambienteInternoPontoForteService.listarTodos();
                setRows(data);
            } catch (error) {
                console.error('Error fetching data:', error);
                toastService.error('Erro ao carregar pontos fortes');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleChange = async (index: number, field: string, value: string | number, save: boolean = true) => {
        const updatedRows = [...rows];
        (updatedRows[index] as any)[field] = value;

        updatedRows[index].pontuacao =
            updatedRows[index].impacto *
            updatedRows[index].diferencial *
            updatedRows[index].tendencia;

        if (save) {
            await salvar(updatedRows[index]);
        }

        setRows(updatedRows);
    };

    const handleAddRow = async () => {
        const novo = {
            descricao: '',
            impacto: 1,
            diferencial: 1,
            tendencia: 1,
            pontuacao: 1,
            acoes: '',
            ordem: rows.length + 1,
        } as AmbienteInternoPontoForte;

        const newRows = [...rows, novo];
        setRows(newRows);

        const newIndex = newRows.length - 1;
        setEditingRow(newIndex);

        setTimeout(() => {
            const el = rowRefs.current[newIndex];
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);
    };

    const handleDeleteRow = async (index: number) => {
        setSaveStatus('saving');
        try {
            if (rows[index].guid) {
                await ambienteInternoPontoForteService.deletar(rows[index].guid!);
            }
            const newRows = [...rows];
            newRows.splice(index, 1);
            newRows.forEach((row, idx) => {
                row.ordem = idx + 1;
            });
            await ambienteInternoPontoForteService.salvarTodos(newRows);
            setRows(newRows);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (error) {
            console.error('Error deleting row:', error);
            setSaveStatus('error');
            toastService.error('Erro ao deletar ponto forte');
        }
    };

    const handleDeleteAll = async () => {
        setOpenConfirmDialog(false);
        setSaveStatus('saving');
        try {
            // falta criar endpoint de deletarTodos
            // await ambienteInternoPontoForteService.deletarTodos();
            setRows([]);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (error) {
            console.error('Error deleting all rows:', error);
            setSaveStatus('error');
            toastService.error('Erro ao excluir todos os Pontos Fortes');
        }
    };

    async function salvar(ambienteInternoPontoForte: AmbienteInternoPontoForte) {
        setSaveStatus('saving');
        try {
            await ambienteInternoPontoForteService.save(ambienteInternoPontoForte);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (error) {
            console.error('Error saving row:', error);
            setSaveStatus('error');
            toastService.error('Erro ao salvar ponto forte');
        }
    }

    return (
        <Box>
            <InfoHeader
                title="Pontos Fortes"
                message="Avalie os pontos fortes de sua empresa. Classifique o impacto, diferencial e tendência para cada item."
                isImportant
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2 }}>
                    <Tooltip title="Adicionar um novo Ponto Forte">
                        <PrimaryButton onClick={handleAddRow}>
                            + Adicionar
                        </PrimaryButton>
                    </Tooltip>
                    <Tooltip title="Excluir todos os Ponto Forte">
                        <PrimaryButton variant="outlined" color="error" onClick={() => setOpenConfirmDialog(true)}>
                            Excluir Tudo
                        </PrimaryButton>
                    </Tooltip>
                </Box>
                <SaveStatusIndicator status={saveStatus} />
            </Box>
            <Grid container sx={{ mt: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <Paper elevation={3}>
                        <TableContainer style={{ borderRadius: '8px' }}>
                            <Table sx={{ minWidth: 700 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell sx={{ width: '54%' }}><strong>Ponto Forte</strong></StyledTableCell>
                                        <StyledTableCell align="center" sx={{ width: '12%' }}>
                                            <InfoPopover
                                                title="Impacto"
                                                description="Quanto este indicador realmente tem impacto sobre o resultado da empresa."
                                                placement="top"
                                            >
                                                <strong>Impacto</strong>
                                            </InfoPopover>
                                        </StyledTableCell>
                                        <StyledTableCell align="center" sx={{ width: '12%' }}>
                                            <InfoPopover
                                                title="Diferencial"
                                                description="Este indicador representa um diferencial competitivo em relação a ação dos nossos concorrentes?"
                                                placement="top"
                                            >
                                                <strong>Diferencial</strong>
                                            </InfoPopover>
                                        </StyledTableCell>
                                        <StyledTableCell align="center" sx={{ width: '12%' }}>
                                            <InfoPopover
                                                title="Tendência"
                                                description="Refere-se à evolução provável do indicador ao longo do tempo, caso nenhuma ação seja tomada. Analisa se o indicador tende a ampliar, permanecer estável ou até regredir."
                                                placement="top"
                                            >
                                                <strong>Tendência</strong>
                                            </InfoPopover>
                                        </StyledTableCell>
                                        <StyledTableCell align="center" sx={{ width: '5%' }}><strong>Pontuação</strong></StyledTableCell>
                                        <StyledTableCell align="center" sx={{ width: '5%' }}><strong>Ações</strong></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <LoadingIndicator message="Carregando Pontos Fortes..." />
                                            </TableCell>
                                        </TableRow>
                                    ) : rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography variant="body1">Nenhum ponto forte encontrado.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((row, index) => (
                                            <StyledTableRow
                                                key={index}
                                                ref={(el) => {
                                                    rowRefs.current[index] = el;
                                                }}
                                            >
                                                <StyledTableCell component="th" scope="row">
                                                    {editingRow === index ? (
                                                        <>
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                minRows={1}
                                                                variant="standard"
                                                                autoFocus
                                                                value={row.descricao}
                                                                onChange={(e) => handleChange(index, 'descricao', e.target.value as string, false)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        if (row.descricao.trim() !== '') {
                                                                            salvar(row);
                                                                            setEditingRow(null);
                                                                        } else {
                                                                            toastService.warning("A descrição não pode estar vazia.");
                                                                        }
                                                                    }
                                                                }}
                                                                onBlur={() => {
                                                                    if (row.descricao.trim() !== '') {
                                                                        salvar(row);
                                                                        setEditingRow(null);
                                                                    } else {
                                                                        toastService.warning("A descrição não pode estar vazia.");
                                                                        const newRows = [...rows];
                                                                        newRows.splice(index, 1);
                                                                        setRows(newRows);
                                                                        setEditingRow(null);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: 16,
                                                                        cursor: 'text',
                                                                        padding: '4px 0px',
                                                                    },
                                                                }}
                                                            />
                                                            <Typography sx={{ color: 'gray', fontSize: '0.75rem', mt: 1 }}>
                                                                Pressione "Enter" ou clique fora para confirmar
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                                                            {row.descricao}
                                                        </Typography>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Select
                                                        value={row.impacto}
                                                        onChange={(e) => handleChange(index, 'impacto', e.target.value)}
                                                        size="small"
                                                        renderValue={(selected) => String(selected)}
                                                    >
                                                        {impactOptions.map((opt) => (
                                                            <MenuItem key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Select<number>
                                                        value={row.diferencial}
                                                        onChange={(e) => handleChange(index, 'diferencial', Number(e.target.value))}
                                                        size="small"
                                                        renderValue={(selected) => String(selected)}
                                                    >
                                                        {diferencialOptions.map((opt) => (
                                                            <MenuItem key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Select<number>
                                                        value={row.tendencia}
                                                        onChange={(e) => handleChange(index, 'tendencia', Number(e.target.value))}
                                                        size="small"
                                                        renderValue={(selected) => String(selected)}
                                                    >
                                                        {tendenciaOptions.map((opt) => (
                                                            <MenuItem key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Typography variant="body2">
                                                        {row.pontuacao}
                                                    </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <BasicMenu
                                                        onEdit={() => setEditingRow(index)}
                                                        onDelete={() => handleDeleteRow(index)}
                                                    />
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza de que deseja excluir todos os pontos fortes? Esta ação não poderá ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton
                        variant="contained"
                        onClick={() => setOpenConfirmDialog(false)}
                    >
                        Cancelar
                    </SecondaryButton>
                    <PrimaryButton
                        color="error"
                        variant="contained"
                        onClick={handleDeleteAll}
                    >
                        Confirmar
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PontosFortes;
